import type { PromptResult, AnalysisMetrics, CompetitorShare } from '@visiward/schemas';
import { positionScoreForResult } from './positionScore.js';

/**
 * Weights for the composite Visibility Score (must sum to 1.0).
 *   25% Mention Rate
 *   20% Recommendation Rate
 *   15% Top-3 Inclusion Rate
 *   10% Position Score
 *   15% Share of Voice
 *   10% Sentiment Score
 *    5% Accuracy Score
 */
export const VISIBILITY_WEIGHTS = {
  mentionRate: 0.25,
  recommendationRate: 0.2,
  top3InclusionRate: 0.15,
  positionScore: 0.1,
  shareOfVoice: 0.15,
  sentimentScore: 0.1,
  accuracyScore: 0.05,
} as const;

function pct(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return clamp((numerator / denominator) * 100, 0, 100);
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function round(v: number, decimals = 1): number {
  const f = Math.pow(10, decimals);
  return Math.round(v * f) / f;
}

function sentimentValue(sentiment: PromptResult['sentiment']): number {
  // Map to a 0–100 scale: positive = 100, neutral = 50, negative = 0.
  switch (sentiment) {
    case 'positive':
      return 100;
    case 'negative':
      return 0;
    default:
      return 50;
  }
}

/**
 * Compute all aggregate metrics for an analysis run from its prompt results.
 * `brandName` is used only for share-of-voice normalization.
 */
export function computeMetrics(
  results: PromptResult[],
  opts: { brandName?: string | null } = {},
): AnalysisMetrics {
  const total = results.length;

  if (total === 0) {
    return emptyMetrics();
  }

  const mentioned = results.filter((r) => r.brandMentioned);
  const recommended = results.filter((r) => r.recommended);
  const top3 = results.filter(
    (r) => r.brandMentioned && r.rankPosition != null && r.rankPosition <= 3,
  );
  const ranked = results.filter((r) => r.rankPosition != null);

  const mentionRate = pct(mentioned.length, total);
  const recommendationRate = pct(recommended.length, total);
  const top3InclusionRate = pct(top3.length, total);

  // Average of per-result position scores.
  const positionScore =
    total === 0
      ? 0
      : round(
          results.reduce(
            (acc, r) =>
              acc + positionScoreForResult({ brandMentioned: r.brandMentioned, rankPosition: r.rankPosition }),
            0,
          ) / total,
        );

  const averagePosition =
    ranked.length === 0
      ? null
      : round(ranked.reduce((acc, r) => acc + (r.rankPosition ?? 0), 0) / ranked.length, 2);

  // Share of voice: brand mentions vs. all brand + competitor mentions.
  const competitorShareOfVoice = computeShareOfVoice(results, opts.brandName ?? null);
  const brandMentionCount = mentioned.length;
  const competitorMentionTotal = competitorShareOfVoice.reduce((acc, c) => acc + c.mentions, 0);
  const shareOfVoice = pct(brandMentionCount, brandMentionCount + competitorMentionTotal);

  // Sentiment: average over results where the brand was mentioned; neutral if none.
  const sentimentSource = mentioned.length > 0 ? mentioned : results;
  const sentimentScore = round(
    sentimentSource.reduce((acc, r) => acc + sentimentValue(r.sentiment), 0) /
      Math.max(1, sentimentSource.length),
  );

  const accuracyScore = round(
    results.reduce((acc, r) => acc + clamp(r.accuracyScore, 0, 100), 0) / total,
  );

  const messageAlignmentScore = round(
    results.reduce((acc, r) => acc + clamp(r.messageAlignment, 0, 100), 0) / total,
  );

  const visibilityScore = round(
    clamp(
      mentionRate * VISIBILITY_WEIGHTS.mentionRate +
        recommendationRate * VISIBILITY_WEIGHTS.recommendationRate +
        top3InclusionRate * VISIBILITY_WEIGHTS.top3InclusionRate +
        positionScore * VISIBILITY_WEIGHTS.positionScore +
        shareOfVoice * VISIBILITY_WEIGHTS.shareOfVoice +
        sentimentScore * VISIBILITY_WEIGHTS.sentimentScore +
        accuracyScore * VISIBILITY_WEIGHTS.accuracyScore,
      0,
      100,
    ),
  );

  return {
    visibilityScore,
    mentionRate: round(mentionRate),
    recommendationRate: round(recommendationRate),
    top3InclusionRate: round(top3InclusionRate),
    averagePosition,
    positionScore,
    shareOfVoice: round(shareOfVoice),
    sentimentScore,
    accuracyScore,
    messageAlignmentScore,
    totalMentions: brandMentionCount,
    totalRecommendations: recommended.length,
    competitorShareOfVoice,
  };
}

export function computeShareOfVoice(
  results: PromptResult[],
  brandName: string | null,
): CompetitorShare[] {
  const counts = new Map<string, number>();
  const brandLower = brandName?.trim().toLowerCase();
  for (const r of results) {
    for (const raw of r.competitors) {
      const name = raw.trim();
      if (!name) continue;
      // Never count the brand itself as a competitor.
      if (brandLower && name.toLowerCase() === brandLower) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  const total = Array.from(counts.values()).reduce((a, b) => a + b, 0);
  return Array.from(counts.entries())
    .map(([name, mentions]) => ({
      name,
      mentions,
      share: total === 0 ? 0 : round((mentions / total) * 100),
    }))
    .sort((a, b) => b.mentions - a.mentions);
}

export function emptyMetrics(): AnalysisMetrics {
  return {
    visibilityScore: 0,
    mentionRate: 0,
    recommendationRate: 0,
    top3InclusionRate: 0,
    averagePosition: null,
    positionScore: 0,
    shareOfVoice: 0,
    sentimentScore: 0,
    accuracyScore: 0,
    messageAlignmentScore: 0,
    totalMentions: 0,
    totalRecommendations: 0,
    competitorShareOfVoice: [],
  };
}
